import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import UnfoldLessDoubleIcon from '@mui/icons-material/UnfoldLessDouble';
import UnfoldMoreDoubleIcon from '@mui/icons-material/UnfoldMoreDouble';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { FormVariant } from '../../forms';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';

import { Dialog } from '../Dialog';
import { SpeedDial } from '../SpeedDial';

import { BackupMode } from './enums/backup-mode.enum';
import { EditorMode } from './enums/editor-mode.enum';
import { useDocumentEditorContext } from './contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { collectAllSectionIds } from './utils/collect-all-section-ids.util';
import type { Editable } from './types/editable.type';

export const Actions = () => {
  const {
    props: {
      formSettings: { formVariant },
      routerSettings: { editorModePaths, gates },
      saveSettings: { backupMode },
    },
    actions: editorActions,
    currentEditorMode,
    state,
    handleSaveAction,
    enrichedVirtualRoot,
  } = useDocumentEditorContext<Editable>();
  const [isVisibleDialog, setIsVisibleDialog] = useState(false);
  const navigate = useNavigate();

  const navigateToInfoEdit = useCallback(
    () => void navigate(editorModePaths.InfoEdit ?? PATH_NOT_FOUND),
    [editorModePaths.InfoEdit, navigate],
  );

  const navigateToInfoEditState = useCallback(
    () => void navigate(editorModePaths.InfoEditState ?? PATH_NOT_FOUND),
    [editorModePaths.InfoEditState, navigate],
  );

  const navigateToAdminEdit = useCallback(
    () => void navigate(editorModePaths.AdminEdit ?? PATH_NOT_FOUND),
    [editorModePaths.AdminEdit, navigate],
  );

  const showDialog = useCallback(() => {
    setIsVisibleDialog(true);
  }, []);

  if (
    [EditorMode.Versions, EditorMode.Preview, EditorMode.Generation].includes(
      currentEditorMode,
    )
  ) {
    return null;
  }

  const baseActions = [
    ...(backupMode === BackupMode.Manual
      ? [
          {
            id: 'save',
            icon: <SaveIcon />,
            name: 'Enregistrer',
            isDisabled:
              state.currentVersion === (state.latestSavedVersion ?? 0),
            onClick: handleSaveAction,
          },
        ]
      : EMPTY_ARRAY),
    {
      id: 'undo',
      icon: <UndoIcon />,
      name: 'Annuler',
      isDisabled: !state.pastVersions.length,
      onClick: editorActions.undo,
    },
    {
      id: 'redo',
      icon: <RedoIcon />,
      name: 'Rétablir',
      isDisabled: !state.futureVersions.length,
      onClick: editorActions.redo,
    },
    {
      id: 'clearHistory',
      icon: <RestorePageIcon />,
      name: 'Réinitialiser',
      isDisabled: !state.pastVersions.length,
      onClick: showDialog,
    },
  ];

  const getEditorActions = () => {
    if (currentEditorMode !== EditorMode.Editor) {
      return EMPTY_ARRAY;
    }

    return [
      {
        id: 'switchToFolded',
        icon: <UnfoldLessDoubleIcon />,
        name: 'Replier',
        isDisabled: !enrichedVirtualRoot.subSections.length,
        onClick: () => {
          editorActions.foldAllSections(
            collectAllSectionIds(enrichedVirtualRoot),
          );
        },
      },
      {
        id: 'switchToUnfolded',
        icon: <UnfoldMoreDoubleIcon />,
        name: 'Déplier',
        isDisabled: !enrichedVirtualRoot.subSections.length,
        onClick: editorActions.unfoldAllSections,
      },
    ];
  };

  const getInfoActions = () => {
    if (
      ![
        EditorMode.Info,
        EditorMode.InfoEdit,
        EditorMode.InfoEditState,
      ].includes(currentEditorMode)
    ) {
      return EMPTY_ARRAY;
    }

    return [
      ...(editorModePaths.InfoEdit
        ? [
            {
              id: 'editGeneralInfo',
              icon: <EditIcon />,
              onClick: navigateToInfoEdit,
              name: 'Modifier Informations',
              gate: gates?.infoEdit,
            },
          ]
        : EMPTY_ARRAY),
      ...(editorModePaths.InfoEditState
        ? [
            {
              id: 'editGeneralInfoState',
              icon: <PublishedWithChangesIcon />,
              onClick: navigateToInfoEditState,
              name: 'Modifier Statut',
              gate: gates?.infoEditState,
            },
          ]
        : EMPTY_ARRAY),
    ];
  };

  const getAdminActions = () => {
    if (![EditorMode.Admin, EditorMode.AdminEdit].includes(currentEditorMode)) {
      return EMPTY_ARRAY;
    }

    return [
      {
        id: 'editAdmin',
        icon: <DisplaySettingsIcon />,
        name: 'Administrer',
        isDisabled: formVariant !== FormVariant.Modification,
        onClick: navigateToAdminEdit,
      },
    ];
  };

  const actions = [
    ...baseActions,
    ...getEditorActions(),
    ...getInfoActions(),
    ...getAdminActions(),
  ];

  return (
    <>
      {!!actions.length && (
        <SpeedDial actions={actions} title="Actions de l'Éditeur de Document" />
      )}
      <Dialog
        content="Êtes-vous sûr de vouloir annuler toutes vos modifications et effacer votre historique de travail ?"
        id="clear-history-dialog"
        isVisible={isVisibleDialog}
        // eslint-disable-next-line react/jsx-handler-names
        onConfirm={editorActions.clearHistory}
        setIsVisible={setIsVisibleDialog}
        title="Réinitialiser"
      />
    </>
  );
};
